/**
 * Indicadores novos do Comercial, do Marketing e do Campo (#6507).
 *   - De onde vêm os contratos: aprovados no mês por origem do lead e a conversão de cada
 *     origem nos últimos 90 dias (Gestão Comercial).
 *   - Rede de parceiros: parceiros ativos no mês, kWh por parceiro e parceiros novos.
 *   - Qualidade do lead: leads de mídia que chegam pelo menos à Qualificação (meta 10%).
 *   - Resultado da semana do Campo: visitas e quantas avançaram no funil.
 * O custo para trazer um cliente (mídia ÷ contratos de mídia) é uma divisão na tela.
 *
 *   node --experimental-strip-types --test src/lib/visaoGeral/__tests__/comercialFase2.test.ts
 */

export const META_QUALIDADE_DO_LEAD = 0.1;

/** Status que mostram que a negociação passou da Qualificação (teve simulação em diante). */
export const STATUS_AVANCADOS = new Set(['simulacao_realizada', 'termo_enviado', 'termo_assinado', 'termo_analise', 'termo_aprovado', 'termo_reprovado']);
/** Etapas do Funil de Vendas da Qualificação em diante, mais as do Pós-Venda. */
export const ETAPAS_QUALIFICADAS = new Set(['Qualificação', 'Em negociação', 'Termo de adesão', 'Análise cadastral', 'Onboarding', 'Acompanhamento', 'Renovação']);

/** A negociação passou da Qualificação: teve simulação em diante ou está numa etapa dali para a frente. */
export const avancouNoFunil = (status: string | null | undefined, stage: string | null | undefined) =>
  STATUS_AVANCADOS.has(status ?? '') || ETAPAS_QUALIFICADAS.has(stage ?? '');

// ─────────────────────────── De onde vêm os contratos ───────────────────────────

/** "Prospecção Geográfica" e "Prospecção geográfica" são a mesma origem. */
const chaveOrigem = (o: string | null | undefined) => (o ?? '').trim().toLowerCase() || '__sem__';

export interface OrigemDoContrato {
  origem: string;
  aprovados: number;
  kwh: number;
  /** Leads criados nos 90 dias até o fim do período e quantos deles já estão aprovados. */
  leads: number;
  aprovadosDosLeads: number;
  conversao: number | null;
}

export function contratosPorOrigem(
  aprovadasNoMes: readonly { origem: string | null; kwh: number | null }[],
  leads90dias: readonly { origem: string | null; aprovado: boolean }[],
): OrigemDoContrato[] {
  const linhas = new Map<string, OrigemDoContrato>();
  const linha = (o: string | null) => {
    const k = chaveOrigem(o);
    let l = linhas.get(k);
    if (!l) { l = { origem: k === '__sem__' ? 'Sem origem' : (o ?? '').trim(), aprovados: 0, kwh: 0, leads: 0, aprovadosDosLeads: 0, conversao: null }; linhas.set(k, l); }
    return l;
  };
  for (const a of aprovadasNoMes) { const l = linha(a.origem); l.aprovados++; l.kwh += Number(a.kwh) || 0; }
  for (const n of leads90dias) { const l = linha(n.origem); l.leads++; if (n.aprovado) l.aprovadosDosLeads++; }
  for (const l of linhas.values()) l.conversao = l.leads ? l.aprovadosDosLeads / l.leads : null;
  return [...linhas.values()].sort((a, b) => b.aprovados - a.aprovados || b.kwh - a.kwh || b.leads - a.leads);
}

// ─────────────────────────── Rede de parceiros ───────────────────────────

export interface ParceiroDaRede { id: string; nome: string | null; criado_em: string | null; status?: string | null }
export interface NegociacaoDeParceiro { responsavel_id: string | null; criada_em: string; aprovadaEm: string | null; kwh: number | null }

export interface LinhaDaRede { parceiro: ParceiroDaRede; leads: number; aprovados: number; kwh: number }

/**
 * Rede de parceiros no período: cada negociação conta para o parceiro dono dela ou dono do
 * vendedor parceiro (`profiles.parceiro_id`). Ativo = criou lead ou teve aprovação no período.
 */
export function redeDeParceiros(
  parceiros: readonly ParceiroDaRede[],
  vendedores: readonly { id: string; parceiro_id: string | null }[],
  negociacoes: readonly NegociacaoDeParceiro[],
  inicio: string,
  fim: string,
): { linhas: LinhaDaRede[]; ativos: number; novos: ParceiroDaRede[]; cadastrados: number } {
  const dono = new Map<string, string>(parceiros.map(p => [p.id, p.id]));
  for (const v of vendedores) if (v.parceiro_id && dono.has(v.parceiro_id)) dono.set(v.id, v.parceiro_id);
  const porParceiro = new Map<string, LinhaDaRede>(parceiros.map(p => [p.id, { parceiro: p, leads: 0, aprovados: 0, kwh: 0 }]));
  const ini = Date.parse(inicio);
  const fimMs = Date.parse(fim);
  const noPeriodo = (iso: string | null) => { if (!iso) return false; const t = Date.parse(iso); return t >= ini && t < fimMs; };
  for (const n of negociacoes) {
    const p = n.responsavel_id ? dono.get(n.responsavel_id) : undefined;
    if (!p) continue;
    const l = porParceiro.get(p)!;
    if (noPeriodo(n.criada_em)) l.leads++;
    if (noPeriodo(n.aprovadaEm)) { l.aprovados++; l.kwh += Number(n.kwh) || 0; }
  }
  const linhas = [...porParceiro.values()].filter(l => l.leads || l.aprovados).sort((a, b) => b.kwh - a.kwh || b.aprovados - a.aprovados || b.leads - a.leads);
  return {
    linhas,
    ativos: linhas.length,
    novos: parceiros.filter(p => noPeriodo(p.criado_em)),
    cadastrados: parceiros.filter(p => (p.status ?? 'active') === 'active').length,
  };
}

// ─────────────────────────── Qualidade do lead ───────────────────────────

/**
 * Lead de mídia qualificado = chegou pelo menos à Qualificação: passou por ela (histórico
 * de etapas), está numa etapa dali em diante ou já teve simulação.
 */
export function qualidadeDoLead(
  leads: readonly { id: number; stage: string | null; status: string | null }[],
  passaramPelaQualificacao: ReadonlySet<number>,
): { leads: number; qualificados: number; taxa: number | null } {
  const qualificados = leads.filter(l => passaramPelaQualificacao.has(l.id) || avancouNoFunil(l.status, l.stage)).length;
  return { leads: leads.length, qualificados, taxa: leads.length ? qualificados / leads.length : null };
}

// ─────────────────────────── Resultado da semana do Campo ───────────────────────────

/** Segunda-feira ('AAAA-MM-DD') da semana de um dia. */
export function inicioDaSemana(iso: string): string {
  const [a, m, d] = iso.slice(0, 10).split('-').map(Number);
  const dt = new Date(a, m - 1, d);
  dt.setDate(dt.getDate() - ((dt.getDay() + 6) % 7));
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

const diaLocal = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export interface SemanaDoCampo { semana: string; visitas: number; comMedidor: number; avancaram: number }

/**
 * Visitas feitas por semana (a mais recente primeiro, `quantas` semanas até a de hoje): quantas
 * pegaram o número do medidor e quantas negociações passaram da Qualificação (simulação ou
 * etapa dali em diante, pela situação de hoje).
 * A mesma negociação visitada duas vezes na semana conta uma vez.
 */
export function resultadoPorSemana(
  visitas: readonly { negociacao_id: number | null; visitado_em: string; numero_medidor: string | null; status_negociacao: string | null; stage_negociacao?: string | null }[],
  hojeIso: string,
  quantas = 4,
): SemanaDoCampo[] {
  const atual = inicioDaSemana(hojeIso);
  const semanas: SemanaDoCampo[] = [];
  for (let i = 0; i < quantas; i++) {
    const [a, m, d] = atual.split('-').map(Number);
    const dt = new Date(a, m - 1, d - 7 * i);
    semanas.push({ semana: `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`, visitas: 0, comMedidor: 0, avancaram: 0 });
  }
  const vistas = new Set<string>();
  for (const v of visitas) {
    const semana = inicioDaSemana(diaLocal(v.visitado_em));
    const s = semanas.find(x => x.semana === semana);
    if (!s) continue;
    const chave = `${semana}|${v.negociacao_id ?? v.visitado_em}`;
    if (vistas.has(chave)) continue;
    vistas.add(chave);
    s.visitas++;
    if ((v.numero_medidor ?? '').trim()) s.comMedidor++;
    if (avancouNoFunil(v.status_negociacao, v.stage_negociacao)) s.avancaram++;
  }
  return semanas;
}
